/**
 * Audio Manager - Manages game sound effects and music
 */

export class AudioManager {
    constructor() {
        this.sounds = {};
        this.enabled = true;
        this.volume = 0.5;

        // Initialize audio context for better control
        this.context = null;
        this.gainNode = null;

        this.initAudioContext();
    }

    initAudioContext() {
        try {
            this.context = new (window.AudioContext || window.webkitAudioContext)();
            this.gainNode = this.context.createGain();
            this.gainNode.connect(this.context.destination);
            this.gainNode.gain.value = this.volume;
        } catch (e) {
            console.warn('Web Audio API not supported');
        }
    }

    /**
     * Load a sound effect
     */
    async loadSound(name, url) {
        try {
            const audio = new Audio(url);
            audio.volume = this.volume;
            audio.preload = 'auto';
            this.sounds[name] = audio;
            return audio;
        } catch (e) {
            console.warn(`Failed to load sound: ${name}`, e);
        }
    }

    /**
     * Play a sound effect
     */
    play(name, options = {}) {
        if (!this.enabled || !this.sounds[name]) return;

        try {
            const audio = this.sounds[name].cloneNode();
            audio.volume = (options.volume || 1) * this.volume;
            audio.playbackRate = options.playbackRate || 1;

            // Play and clean up
            audio.play().catch(e => {
                // Auto-play might be blocked, ignore error
            });

            // Remove after playing
            audio.addEventListener('ended', () => {
                audio.remove();
            });
        } catch (e) {
            console.warn(`Failed to play sound: ${name}`, e);
        }
    }

    /**
     * Play cannon hit sound effect
     */
    playCannonHit() {
        // Create synthetic sound using Web Audio API
        if (!this.context) return;

        try {
            const oscillator = this.context.createOscillator();
            const gainNode = this.context.createGain();
            const filter = this.context.createBiquadFilter();

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(150, this.context.currentTime);
            oscillator.frequency.exponentialRampToValueAtTime(50, this.context.currentTime + 0.2);

            filter.type = 'lowpass';
            filter.frequency.value = 400;

            gainNode.gain.setValueAtTime(0.3 * this.volume, this.context.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.2);

            oscillator.connect(filter);
            filter.connect(gainNode);
            gainNode.connect(this.context.destination);

            oscillator.start();
            oscillator.stop(this.context.currentTime + 0.2);
        } catch (e) {
            console.warn('Failed to play cannon hit sound', e);
        }
    }

    /**
     * Set master volume
     */
    setVolume(volume) {
        this.volume = Math.max(0, Math.min(1, volume));
        if (this.gainNode) {
            this.gainNode.gain.value = this.volume;
        }

        // Update all loaded sounds
        Object.values(this.sounds).forEach(sound => {
            if (sound instanceof Audio) {
                sound.volume = this.volume;
            }
        });
    }

    /**
     * Enable/disable all sounds
     */
    setEnabled(enabled) {
        this.enabled = enabled;
    }

    /**
     * Stop all sounds
     */
    stopAll() {
        Object.values(this.sounds).forEach(sound => {
            if (sound instanceof Audio) {
                sound.pause();
                sound.currentTime = 0;
            }
        });
    }
}